import React from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningIcon from "@mui/icons-material/Warning";

interface DealDetailPageProps {
  dealData: any;
  onClose: () => void;
}

const DealDetailPage: React.FC<DealDetailPageProps> = ({
  dealData,
  onClose,
}) => {
  if (!dealData) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: "1500px",
          margin: "auto",
          mt: "10%",
          p: 3,
          textAlign: "center",
          borderRadius: 2,
          boxShadow: 3,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" color="primary" fontWeight="bold" mb={2}>
          Deal Details
        </Typography>
        <Typography variant="body1" color="textSecondary">
          No deal data available.
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          sx={{ mt: 3 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const {
    title,
    category,
    subCategoryId,
    area,
    offer,
    offerType,
    description,
    termsAndConditions,
    duration,
    discountBracket,
    repeatDuration,
    image,
    businessProfile,
    slug,
    clickCount,
    activityStatus,
    deleted,
    metaTitle,
    metaDescription,
    isPromoted,
    validFor,
    discountType,
    discountPrice,
    currentPrice,
    discountPercentage,
    startDate,
    endDate,
    startTime,
    endTime,
    days,
    daysNote,
    status,
  } = dealData;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1200px",
        margin: "auto",
        mt: 5,
        p: 3,
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: 3,
      }}
    >
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" color="primary" fontWeight="bold">
          Deal Detail
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* Left Section */}
        <Grid item xs={12} md={6}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Business : {businessProfile?.businessName}
              <span>
                {status === "APPROVED" ? (
                  <Tooltip title="Approved">
                    <IconButton aria-label="status" style={{ color: "#039487" }}>
                      <TaskAltIcon />
                    </IconButton>
                  </Tooltip>
                ) : status === "PENDING" ? (
                  <Tooltip title="Pending">
                    <IconButton aria-label="status" style={{ color: "#ff9966" }}>
                      <WarningIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Rejected">
                    <IconButton aria-label="status" style={{ color: "#ff0000" }}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </span>
            </Typography>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Deals Titel : {title}
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              <strong>Slug : </strong> {slug}
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              <strong>Category : </strong> {category}
            </Typography>
            <Chip
              label={`${discountPrice} ₹`}
              color="primary"
              sx={{ mt: 2, fontSize: "0.9rem", fontWeight: "bold" }}
            />
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              <strong>Offer Type : </strong> {discountType}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              <strong>Current Price : </strong> {discountType === "PERCENTAGE" ? `₹${currentPrice} (${discountPercentage}%)`
                : "-"}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Discount Price : </strong>{discountPrice}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Description : </strong> {description}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              <strong>Area : </strong> {area}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Start Date : </strong>{startDate ? new Date(startDate).toLocaleDateString() : "No Start Date"}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>End Date : </strong> {endDate ? new Date(endDate).toLocaleDateString() : "No End Date"}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Start Time : </strong>{startTime}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>End Time : </strong>{endTime}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Click Count : </strong> {clickCount}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Activity Status : </strong> {activityStatus}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Deleted : </strong> {deleted ? "Yes" : "No"}
            </Typography>
          </CardContent>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={6}>
          <CardMedia
            component="img"
            image={businessProfile?.brandLogo || "https://via.placeholder.com/500x300"}
            alt="Deal Image"
            sx={{
              width: "100%",
              height: "auto",
              borderRadius: 2,
            }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end">
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default DealDetailPage;